import { Text, ButtonGroup } from "@chakra-ui/react";
import { AccountPermission } from "@syla/shared/types/models/AccountAccessBase";
import { getCurrentSubscription } from "@syla/shared/types/subscriptions/getCurrentSubscription";
import { getSubscriptionState } from "@syla/shared/types/subscriptions/getSubscriptionState";
import { Lifecycle } from "@syla/shared/types/subscriptions/subscriptionState";
import React from "react";
import { getUserDetails } from "../../../helper/getUserDetails";
import { hasAccountPermission } from "../../../helper/permissions";
import { useCurrentAccountStore } from "../../../store/currentAccountStore";
import { useQueryGetAccount } from "../../../store/useQueryGetAccount";
import { useQuerySubscriptions } from "../../../store/useQuerySubscriptions";
import { Subscription } from "../../../types/plans/subscription";
import { SettingRowBase } from "../components/SettingRowBase";
import { ManageBillingButton } from "./ManageBillingButton";
import { RemoveBillingOwnerButton } from "./RemoveBillingOwnerButton";

import { ValueAndButtons } from "./ValueAndButtons";

export function BillingOwnerDetails() {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const { data: account } = useQueryGetAccount(accountId);

  const { data: subscriptions } = useQuerySubscriptions();

  if (!subscriptions || !account) {
    return null;
  }

  const subscription = getCurrentSubscription(subscriptions.subscriptions);

  const showManageBillingButton =
    subscription?.stripeSubscriptionId && subscription.billingAccountAccess;

  const showRemoveBillingOwnerButton =
    subscription &&
    !subscription.billingAccountAccess &&
    subscription.billingOwner &&
    getSubscriptionState(subscription).lifecycle !== Lifecycle.Terminated &&
    hasAccountPermission(account, AccountPermission.ChangeAccess);

  return (
    <SettingRowBase
      label="Billing account owner"
      description="The person responsible for paying the subscription on this account."
    >
      <ValueAndButtons>
        <Text>{getBillingOwnerLabel(subscription)}</Text>
        <ButtonGroup>
          {showManageBillingButton && (
            <ManageBillingButton accountId={accountId} />
          )}

          {showRemoveBillingOwnerButton && subscription?.billingOwner && (
            <RemoveBillingOwnerButton
              accountId={accountId}
              billingOwnerId={subscription.billingOwner._id}
            />
          )}
        </ButtonGroup>
      </ValueAndButtons>
    </SettingRowBase>
  );
}

function getBillingOwnerLabel(subscription?: Subscription) {
  if (!subscription?.billingOwner) {
    return "None";
  }

  // If the subscription is terminated, it may be because the 'Remove Billing Owner'
  // button was used, in which case we don't want to show the old billing owner.
  // If the subscription was terminated in any other way, then it's also not
  // that helpful to show the billing owner on an expired subscription.
  const state = getSubscriptionState(subscription);
  if (state.lifecycle === Lifecycle.Terminated) {
    return "None";
  }

  if (subscription.billingAccountAccess) {
    return "You";
  }
  return getUserDetails(subscription.billingOwner);
}
